// Bloque de la home «Cómo usa InvokeApp tu cuenta de Google» / «How InvokeApp uses your Google
// account». Lo exige la verificación de Google: la home tiene que explicar por qué la app pide datos
// de la cuenta de Google y enlazar la política de privacidad (docs/legal/PLAN-CASA-TECH-PROVIDER.md,
// fila 5; docs/legal/GOOGLE-VERIFICACION-OAUTH.md).
//
// Es texto con valor legal: tiene que coincidir con /privacy §5 y con lo que hace la plataforma en
// producción. Por eso las frases que dependen de controles que aún no están desplegados van detrás
// de CLAIMS: cada una se publica SOLO cuando su control está en producción. Hasta entonces se
// muestra la frase que hoy es cierta.

export const CLAIMS = {
  // Fila 29 del plan: cada respuesta redactada por la IA en un buzón con lectura la aprueba una
  // persona (sin modo autónomo de respuesta). Pasar a true al desplegar ese control en el core.
  humanApprovalOfEveryReply: false,
  // Fila 6 del plan (G1): por Gmail solo se escribe a clientes y a contactos con permiso. Pasar a
  // true al desplegar el filtro en el motor de outreach.
  consentOnlyGmailSending: false,
  // Fila 8 del plan (G2): Model Armor delante de los puntos donde el correo leído llega a la IA.
  // Pasar a true al desplegar el filtro y crear la plantilla en invokeapp-prod.
  promptInjectionFilter: false,
};

const PRIVACY_PATH = { es: '/privacy', en: '/privacy?lang=en' };
const LIMITED_USE_URL = 'https://developers.google.com/terms/api-services-user-data-policy';

const TEXT = {
  es: {
    kicker: 'Datos de Google',
    title: 'Cómo usa InvokeApp tu cuenta de Google',
    intro: 'InvokeApp solo accede a una cuenta de Google si su titular conecta el buzón de Gmail en la '
      + 'plataforma (el de la empresa o el de un proyecto) o en el portal de su agencia, y solo para '
      + 'las funciones que se ven en la plataforma.',
    send: {
      title: 'Enviar correos desde tu cuenta',
      scope: 'gmail.send',
      base: 'Se pide al conectar el buzón. InvokeApp envía desde tu propia dirección los correos de '
        + 'tus campañas, seguimientos y respuestas.',
      consentOnly: 'Solo a tus clientes y a contactos que han dado permiso para recibirlos.',
      tail: 'Los correos de campaña incluyen un enlace para darse de baja.',
    },
    read: {
      title: 'Leer solo las respuestas',
      scope: 'gmail.readonly',
      base: 'Solo si activas «Permitir leer respuestas». InvokeApp lee únicamente los hilos que se '
        + 'iniciaron desde InvokeApp, para mostrarte la respuesta, detectar las bajas y detener los '
        + 'seguimientos pendientes. No lee el resto de tu buzón ni los adjuntos.',
    },
    ai: {
      title: 'IA con supervisión humana',
      base: 'La IA (Gemini, en la Unión Europea) clasifica cada respuesta y propone un borrador de '
        + 'contestación.',
      approvalLive: 'Una persona lo revisa y lo aprueba antes de enviarlo.',
      approvalDefault: 'Por defecto, una persona revisa cada borrador antes de enviarlo.',
      injection: 'Antes de llegar a la IA, el texto de los correos pasa por un filtro contra '
        + 'instrucciones maliciosas (Model Armor de Google Cloud).',
      tail: 'No usamos tus correos para entrenar modelos de IA ni para publicidad, y no los vendemos.',
    },
    control: {
      title: 'Tú decides',
      base: 'Puedes desconectar el buzón en Integraciones (revocamos el permiso ante Google) o quitar '
        + 'el acceso en myaccount.google.com/permissions. Para borrar los datos ya guardados, pídelo en '
        + 'invokeapp.io/data-deletion: los borramos en un máximo de 30 días.',
    },
    limitedUse: 'El uso que InvokeApp hace de la información recibida de las API de Google cumple la '
      + 'Google API Services User Data Policy, incluidos los requisitos de uso limitado.',
    limitedUseLink: 'Ver la política de Google',
    privacyLink: 'Lee el detalle en la Política de privacidad (§5, Datos de Google)',
  },
  en: {
    kicker: 'Google user data',
    title: 'How InvokeApp uses your Google account',
    intro: 'InvokeApp only accesses a Google account if its owner connects the Gmail mailbox in the '
      + 'platform (the company\'s or a project\'s mailbox) or in their agency\'s portal, and only for '
      + 'the features shown in the platform.',
    send: {
      title: 'Send emails from your account',
      scope: 'gmail.send',
      base: 'Requested when you connect the mailbox. InvokeApp sends, from your own address, the '
        + 'emails of your campaigns, follow-ups and replies.',
      consentOnly: 'Only to your customers and to contacts who have agreed to receive them.',
      tail: 'Campaign emails include an unsubscribe link.',
    },
    read: {
      title: 'Read replies only',
      scope: 'gmail.readonly',
      base: 'Only if you turn on "Allow reading replies". InvokeApp reads only the threads that were '
        + 'started from InvokeApp, to show you the reply, detect unsubscribe requests and stop pending '
        + 'follow-ups. It does not read the rest of your mailbox or any attachments.',
    },
    ai: {
      title: 'AI with human oversight',
      base: 'AI (Gemini, in the European Union) classifies each reply and suggests a draft answer.',
      approvalLive: 'A person reviews and approves it before it is sent.',
      approvalDefault: 'By default, a person reviews each draft before it is sent.',
      injection: 'Before it reaches the AI, the email text goes through a filter against malicious '
        + 'instructions (Google Cloud Model Armor).',
      tail: 'We do not use your emails to train AI models or for advertising, and we do not sell them.',
    },
    control: {
      title: 'You stay in control',
      base: 'You can disconnect the mailbox in Integrations (we revoke the permission with Google) or '
        + 'remove access at myaccount.google.com/permissions. To delete data already stored, request it '
        + 'at invokeapp.io/data-deletion: we delete it within 30 days at most.',
    },
    limitedUse: 'InvokeApp\'s use of information received from Google APIs adheres to the Google API '
      + 'Services User Data Policy, including the Limited Use requirements.',
    limitedUseLink: 'Read Google\'s policy',
    privacyLink: 'Read the details in the Privacy Policy (section 5, Google user data)',
  },
};

const join = (...parts) => parts.filter(Boolean).join(' ');

// Contenido listo para pintar, en el idioma pedido y con las frases que permite CLAIMS.
export const buildGoogleDataUse = (language, claims = CLAIMS) => {
  const lang = typeof language === 'string' && language.startsWith('en') ? 'en' : 'es';
  const tx = TEXT[lang];
  return {
    lang,
    kicker: tx.kicker,
    title: tx.title,
    intro: tx.intro,
    items: [
      {
        key: 'send',
        title: tx.send.title,
        scope: tx.send.scope,
        text: join(tx.send.base, claims.consentOnlyGmailSending && tx.send.consentOnly, tx.send.tail),
      },
      { key: 'read', title: tx.read.title, scope: tx.read.scope, text: tx.read.base },
      {
        key: 'ai',
        title: tx.ai.title,
        text: join(
          tx.ai.base,
          claims.humanApprovalOfEveryReply ? tx.ai.approvalLive : tx.ai.approvalDefault,
          claims.promptInjectionFilter && tx.ai.injection,
          tx.ai.tail,
        ),
      },
      { key: 'control', title: tx.control.title, text: tx.control.base },
    ],
    limitedUse: tx.limitedUse,
    limitedUseLink: tx.limitedUseLink,
    limitedUseUrl: LIMITED_USE_URL,
    privacyLink: tx.privacyLink,
    privacyPath: PRIVACY_PATH[lang],
  };
};

export default buildGoogleDataUse;
